// src/utils/normalizeEvent.js

/**
 * normalizeEvent
 * -----------------------------------------------------------------------------
 * WHY:
 * - Historical/legacy rows and different writers have produced slight schema
 *   drift in `hosting` rows (e.g., some use `host_id`, others `user_id`).
 * - `event_link` may be stored as an array or as a JSON string.
 * - Components (EventViewer, AttendingEvents, etc.) expect a consistent shape.
 *
 * WHAT THIS DOES:
 * - Ensures `event.host_id` is always present by falling back to `user_id`.
 * - Normalizes `event.event_link` to an array (parsing JSON strings safely).
 * - Returns a shallow copy; does NOT mutate the original object or the DB.
 *
 * HOW TO USE:
 *   import { normalizeEvent } from '../utils/normalizeEvent';
 *   const event = normalizeEvent(rowFromSupabase);
 *   // Now `event.host_id` and `event.event_link` are safe to consume.
 *
 * NOTES:
 * - This is a pure UI-layer adapter for backward compatibility.
 * - If more fields need coercion (dates to Date, booleans, etc.), add them here
 *   so consumers stay simple and consistent across the app.
 */

export const normalizeEvent = (raw) => {
  if (!raw) return raw;

  const normalizedHostId = raw.host_id ?? raw.user_id ?? null;

  const parseLinks = (val) => {
    try {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string' && val.trim().startsWith('[')) {
        return JSON.parse(val);
      }
    } catch (e) {
      console.warn('⚠️ Could not parse event_link JSON:', e);
    }
    return [];
  };

  return {
    ...raw,
    host_id: normalizedHostId,
    event_link: parseLinks(raw.event_link),
  };
};
